import { Clear, Tune } from "@mui/icons-material";
import { Button, ClickAwayListener, Divider, Fade, Paper, Popper, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { ORDER_SOURCE_TYPE, orderStatusFormatted } from "src/constant";
import useTPList from "src/hooks/setting/transportation-provider/useTPList";
import useQueryParams from "src/hooks/useQueryParams";

const tpParams = { limit: 200, offset: 0 };

const sources = [
  { _id: ORDER_SOURCE_TYPE.Manual, name: ORDER_SOURCE_TYPE.Manual },
  { _id: ORDER_SOURCE_TYPE.SalesChannel, name: ORDER_SOURCE_TYPE.SalesChannel },
];

const FILTER_KEYS = {
  default: ["source", "shippingProvider", "statusIn"],
  "delivery-all": ["shippingProvider"],
};

/**
 * Bộ lọc dành riêng cho quản lý đơn hàng.
 * @param {"default"|"delivery-all"} variant
 */
export default function OrderFilter({ setParamsQuery, paramsQuery, variant = "default" }) {
  const [option] = useQueryParams();
  const isDeliveredFailedTab = variant === "default" && option?.tab === orderStatusFormatted.DeliveredFailed;

  const { data: tpList } = useTPList(variant === "delivery-all" || variant === "default" ? tpParams : null);

  const [popperProps, setPopperProps] = useState({
    open: false,
    anchorEl: null,
    placement: "bottom-end",
  });

  const handlePickOptionSearch = ({ name, value }) => {
    setParamsQuery({
      ...paramsQuery,
      [name]: value === ORDER_SOURCE_TYPE.All ? undefined : value,
    });
  };

  const isTatCaActiveForFilterGroup = (groupName) => {
    return [ORDER_SOURCE_TYPE.All, undefined]?.includes(paramsQuery?.[groupName]);
  };

  const filterList = useMemo(() => {
    if (isDeliveredFailedTab) {
      return [];
    }

    if (variant === "delivery-all") {
      return [
        {
          id: "tp",
          name: "shippingProvider",
          tatCaKey: "shippingProvider",
          title: "Đơn vị vận chuyển",
          data: Array.isArray(tpList?.data) ? tpList.data : [],
        },
      ];
    }

    return [
      {
        id: "source",
        name: "source",
        tatCaKey: "source",
        title: "Nguồn",
        data: sources,
      },
      {
        id: "tp",
        name: "shippingProvider",
        tatCaKey: "shippingProvider",
        title: "Đơn vị vận chuyển",
        data: Array.isArray(tpList?.data) ? tpList.data : [],
      },
      // {
      //   id: "warehouseIn",
      //   name: "statusIn",
      //   tatCaKey: "statusIn",
      //   title: "Trạng thái",
      //   data: [
      //     { _id: orderStatusFormatted.Pending, name: "Chờ nhập kho" },
      //     { _id: orderStatusFormatted.Imported, name: "Đã nhập kho" },
      //   ],
      // },
    ];
  }, [isDeliveredFailedTab, variant, tpList?.data]);

  const activeFilterKeys = FILTER_KEYS[variant] ?? FILTER_KEYS.default;

  const handleSetPop = (placement) => (event) => {
    setPopperProps({
      open: !popperProps.open,
      anchorEl: event.currentTarget,
      placement,
    });
  };

  const handleClosePopper = () => {
    setPopperProps((prev) => ({ ...prev, open: false }));
  };

  const hasActiveFilters = useMemo(() => {
    return activeFilterKeys.some((k) => {
      const val = paramsQuery?.[k];
      return val !== undefined && val !== null && val !== "";
    });
  }, [paramsQuery, activeFilterKeys]);

  const handleClearFilters = () => {
    setParamsQuery((prev) => {
      const next = { ...(prev ?? {}) };
      activeFilterKeys.forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  if (isDeliveredFailedTab) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={handleClosePopper}>
      <Stack sx={{ display: "inline-block" }}>
        <Button
          size="small"
          variant="outlined"
          sx={{ height: "100%", maxHeight: "36.5px" }}
          startIcon={<Tune />}
          onClick={handleSetPop("bottom-end")}
        >
          Lọc
        </Button>
        <Popper
          disablePortal
          sx={{
            zIndex: 1200,
            maxWidth: "900px",
            width: "90%",
            maxHeight: "330px",
            height: "auto",
            overflow: "auto",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          }}
          open={popperProps.open}
          anchorEl={popperProps.anchorEl}
          placement={popperProps.placement}
          transition
        >
          {({ TransitionProps }) => (
            <Fade
              {...TransitionProps}
              timeout={350}
            >
              <Paper sx={{ p: "12px", display: "flex", flexDirection: "column" }}>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  sx={{ pb: 1, mb: 1, borderBottom: "1px solid var(--light-grey)" }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<Clear />}
                    disabled={!hasActiveFilters}
                    onClick={handleClearFilters}
                    sx={{ fontSize: "12px" }}
                  >
                    Xóa bộ lọc
                  </Button>
                </Stack>
                {filterList.map((v) => (
                  <Stack
                    direction="column"
                    key={v.id}
                    spacing={1}
                    mb={1.5}
                    pd={1.5}
                  >
                    <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>{v.title}</Typography>
                    <Stack
                      direction="row"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Button
                        onClick={() =>
                          handlePickOptionSearch({
                            name: v.tatCaKey,
                            value: ORDER_SOURCE_TYPE.All,
                          })
                        }
                        sx={{
                          border: isTatCaActiveForFilterGroup(v.tatCaKey) ? "1px solid var(--confirm-txt)" : "1px solid var(--light-grey)",
                          color: isTatCaActiveForFilterGroup(v.tatCaKey) ? "var(--confirm-txt)" : "var(--black)",
                          display: "inline",
                          borderRadius: "4px",
                          fontSize: "12px",
                        }}
                      >
                        Tất cả
                      </Button>
                      {v?.data?.map((val) => (
                        <Button
                          key={val?._id}
                          onClick={() =>
                            handlePickOptionSearch({
                              name: v.name,
                              value: val?._id,
                            })
                          }
                          sx={{
                            border: [val?._id]?.includes(paramsQuery?.[v.name]) ? "1px solid var(--confirm-txt)" : "1px solid var(--light-grey)",
                            color: [val?._id]?.includes(paramsQuery?.[v.name]) ? "var(--confirm-txt)" : "var(--black)",
                            display: "inline",
                            p: "6px 12px !important",
                            borderRadius: "4px",
                            fontSize: "12px",
                          }}
                        >
                          {val?.name}
                        </Button>
                      ))}
                    </Stack>
                    <Divider sx={{ color: "var(--light-grey)" }} />
                  </Stack>
                ))}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Stack>
    </ClickAwayListener>
  );
}
